import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Loading from 'components/Loading';
import { getImputationAsString } from 'routes/BudgetModule/config/hooks';
import Typography from '@mui/material/Typography';
import InscriptionsGrid from 'routes/BudgetModule/components/shared/InscriptionsGrid/InscriptionsGrid';
import {
    BudgetProposalT,
    ResourceBudgetProposalAssociationT,
} from 'routes/BudgetModule/config/types';
import { getInscriptionsGridColumns } from 'routes/BudgetModule/components/shared/InscriptionsGrid/gridHelpers';
import { getResourceNameField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import AddInscription from 'routes/BudgetModule/components/shared/InscriptionsGrid/AddInscription/AddInscription';
import { translateBlockValue } from 'i18n';
import { ImputationDimensionsContext } from 'routes/BudgetModule/components/BudgetWithProvider/ImputationDimensionsContext';
import { FEATURE_ORPHAN_INSCRIPTION, isEnabled } from 'helpers/features';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useGetProjects } from 'routes/BudgetModule/shared/useGetProjects';

import { getOperationUrl } from '../helpers/url';
import {
    getInscriptionsGridColumnsForOperation360,
    getOrderedColumns,
} from './helpers/gridHelpers';
import { PADDING_TITLE } from '../helpers/constants';
import { getAddInscriptionStepTitles } from './helpers/typographyHelpers';
import {
    hasInscriptionBeenSetToRightPhase,
    isInscriptionBelongsToActiveBudgetProposal,
} from '../helpers/dataHelpers';
import { useGetOperationInscriptions } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import { buildInscriptionsWithPhase } from 'routes/BudgetModule/helpers/inscriptionsFetchUtils';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import {
    ALL_OPERATION_INSCRIPTIONS_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import TabActions from '../../BudgetOverview/TabActions';
import GridApiProvider from 'routes/BudgetModule/contexts/GridApiProvider';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type OperationInscriptionsProps = {
    operationInfos: {
        operationSoftwareId: string;
        operationName: string;
        isDraft?: boolean;
    };
    budgetProposals: BudgetProposalT[];
    expenseOrIncome: BudgetExpenseOrIncome;
    budgetProposalResources: ResourceBudgetProposalAssociationT[];
    budgetBy: BudgetBys;
};

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    marginRight: 0,
    '&&& .ag-theme-material': {
        marginLeft: '0 !important',
    },
});

const StyCard = styled(Box, {
    name: 'StyCard',
})({
    marginBottom: '5rem',
});

export default function OperationInscriptions({
    budgetProposals,
    budgetProposalResources,
    operationInfos,
    budgetBy,
    expenseOrIncome,
}: OperationInscriptionsProps): React.ReactElement {
    const { t } = useTranslation();

    const { operationSoftwareId: operationSoftwareIdParam = '' } = useParams();

    const encodedOperationSoftwareId = encodeURIComponent(
        operationSoftwareIdParam,
    );

    const {
        operationSoftwareId,
        operationName,
        isDraft = false,
    } = operationInfos;

    const resourceNameField = getResourceNameField(budgetBy);

    // We need to refetch the project here
    // as they are needed in the grid
    const { projects } = useGetProjects();

    const imputationRawDimensions = useContext(ImputationDimensionsContext);

    const {
        data: inscriptionsData,
        isLoading: isInscriptionsLoading,
        cacheKey: queryCacheKey,
    } = useGetOperationInscriptions({
        budgetBy,
        expenseOrIncome,
        [isDraft
            ? 'enrichedOperationSoftwareId'
            : 'secondaryBudgetAxisElementSoftwareId']: operationSoftwareId,
    });

    const inscriptions = inscriptionsData?.data.filter(
        (inscription) =>
            isInscriptionBelongsToActiveBudgetProposal(
                inscription,
                budgetProposals,
            ) && hasInscriptionBeenSetToRightPhase(inscription),
    );

    const inscriptionsWithPhase = buildInscriptionsWithPhase({
        inscriptions,
        budgetProposals,
    });

    const imputationAsString = getImputationAsString(imputationRawDimensions);

    const columns = getOrderedColumns(
        [
            ...getInscriptionsGridColumns(
                t,
                budgetProposals,
                imputationAsString,
                false,
                projects,
            ),
            ...getInscriptionsGridColumnsForOperation360(
                t,
                resourceNameField,
                budgetProposals,
            ),
        ],
        resourceNameField,
    );

    const resourceName = translateBlockValue(resourceNameField, true);

    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const addInscriptionStepTitles = getAddInscriptionStepTitles(
        t,
        resourceName,
        operationName,
        sourceTable,
    );

    const isEnabledOrphanInscription = isEnabled(FEATURE_ORPHAN_INSCRIPTION);
    const onCloseModal = (
        isInscriptionOrphan: boolean,
        selectedBudgetProposal: BudgetProposalT,
        selectedResourceName: string,
    ) => {
        const events = [];
        events.push(
            TRACKABLE_EVENTS.budget.general.operation360.addInscription,
        );
        if (isInscriptionOrphan)
            events.push(
                TRACKABLE_EVENTS.budget.general.operation360
                    .addSemiOrphanInscription,
            );

        events.forEach((event) => {
            trackEventBudget(event, {
                budgetProposalName: selectedBudgetProposal.name,
                phase: selectedBudgetProposal.phase,
                resourceName: selectedResourceName,
            });
        });
    };

    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: getOperationUrl(
                encodedOperationSoftwareId,
                BudgetExpenseOrIncome.EXPENSE,
            ),
        },
        {
            label: t('budget.income'),
            pathname: getOperationUrl(
                encodedOperationSoftwareId,
                BudgetExpenseOrIncome.INCOME,
            ),
        },
    ].map((tablist) => {
        const exportConfig = {
            fileName: getExcelFilename(ALL_OPERATION_INSCRIPTIONS_FILE_PREFIX, [
                expenseOrIncome,
            ]),
            sheetName: t('budgetOverview.allInscriptions'),
        };

        return {
            ...tablist,
            key: tablist.label,
            tabActions: (
                <TabActions
                    trackedEvents={
                        TRACKABLE_EVENTS.budget.budgetOverview.operations
                            .inscriptions
                    }
                    hideEmpty={false}
                    toggleLabel={t(
                        'budgetProposal.hideEmptySecondaryBudgetAxisElements',
                    )}
                    fileName={exportConfig.fileName}
                />
            ),
            children: isInscriptionsLoading ? (
                <Loading />
            ) : (
                <StyGrid>
                    <InscriptionsGrid
                        inscriptions={inscriptionsWithPhase}
                        nbDisplayedInscriptions={
                            // the -2.8 is because the header size is reduced on this view (and padding)
                            Math.min(inscriptionsWithPhase.length, 10) - 2.8
                        }
                        columns={columns}
                        expenseOrIncome={expenseOrIncome}
                        budgetProposals={budgetProposals}
                        queryCacheKey={queryCacheKey}
                        allowFloatingFilters
                        pagination
                        paginationPageSize={10}
                        showRowGroupPanel
                        excelExport={exportConfig}
                    />
                    <AddInscription
                        budgetProposalResources={budgetProposalResources.filter(
                            (budgetProposalResource) =>
                                budgetProposalResource.canAddInscription,
                        )}
                        stepTitles={addInscriptionStepTitles}
                        resourceType={resourceNameField}
                        expenseOrIncome={expenseOrIncome}
                        allowOrphanInscription={isEnabledOrphanInscription}
                        operationSoftwareId={operationSoftwareId}
                        onCloseModal={onCloseModal}
                        isOperationDraft={isDraft}
                        inscriptionQueryCacheKey={queryCacheKey}
                    />
                </StyGrid>
            ),
            disabled: false,
        };
    });

    return (
        <StyCard>
            <Typography sx={{ ...PADDING_TITLE }} variant="h5">
                {t('secondaryBudgetAxisElement360.inscriptionDetails')}
            </Typography>
            <Card>
                <CardContent>
                    <GridApiProvider>
                        <AuxiliaryTabs tabsList={tabsList} />
                    </GridApiProvider>
                </CardContent>
            </Card>
        </StyCard>
    );
}
